import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/reducers/toasterSlice";
import { showSpinner, hideSpinner } from "../../../store/reducers/spinnerSlice";
import { RestaurantService } from "../../../utils/api.service";
import { getStoreInfo } from "../../../utils/_hooks";
import Button from "../../../components/core/Element/Button/Button";
import { Upload, Palette } from "lucide-react";
import ImageCropModal from "../../../components/core/Element/Upload/components/ImageCropModal";

const HeaderFavicon = () => {
  const dispatch = useDispatch();
  const store = getStoreInfo();

  const [formData, setFormData] = useState({
    name: store?.name || "",
    primaryColor: store?.primaryColor || "#6b21a8",
    secondaryColor: store?.secondaryColor || "#f3e8ff",
    primaryTextColor: store?.primaryTextColor || "#ffffff",
    secondaryTextColor: store?.secondaryTextColor || "#1f2937",
  });

  const [logoPreview, setLogoPreview] = useState(store?.logo || null);

  const [isCropOpen, setIsCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);

  const handleColorChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      dispatch(
        showToast({
          type: "error",
          title: "Error",
          message: "Please upload an image file",
        })
      );
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    setCropImageSrc(fileUrl);
    setIsCropOpen(true);
    if (e.target) e.target.value = "";
  };

  const handleCropComplete = async (croppedUrl) => {
    try {
      dispatch(showSpinner());
      const storeId = store?._id || store?.id;
      if (!storeId) {
        dispatch(
          showToast({
            type: "error",
            title: "Error",
            message: "Store ID not found. Please refresh and try again.",
          })
        );
        return;
      }

      const resBlob = await fetch(croppedUrl);
      const blob = await resBlob.blob();
      const fileBlob = new File([blob], "store-logo.png", { type: "image/png" });

      const data = new FormData();
      data.append("file", fileBlob);
      data.append("logo", fileBlob);

      const res = await RestaurantService.changeLogo(storeId, data);
      if (res?.status === 200 || res?.status === 201) {
        setLogoPreview(res?.data?.logo);
        const updatedStore = {
          ...(typeof store === "object" ? store : {}),
          logo: res?.data?.logo,
        };
        localStorage.setItem("storeInfo", JSON.stringify(updatedStore));
        dispatch(
          showToast({
            type: "success",
            title: "Success",
            message: res?.data?.message || "Store logo updated successfully!",
          })
        );
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to upload store logo.";
      dispatch(
        showToast({
          type: "error",
          title: "Error",
          message: errMsg,
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const [taxSettings, setTaxSettings] = useState({
    isGstEnabled: store?.taxSettings?.isGstEnabled || false,
    gstNumber: store?.taxSettings?.gstNumber || "",
    defaultGstRate: store?.taxSettings?.defaultGstRate || 5,
    gstMode: store?.taxSettings?.gstMode || "EXCLUSIVE",
  });

  const handleTaxSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxSettings({
      ...taxSettings,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSaveTheme = async () => {
    try {
      dispatch(showSpinner());
      const payload = {
        ...formData,
        taxSettings,
      };
      const res = await RestaurantService.updateRestaurant(store?._id, payload);
      if (res?.status === 200) {
        const updatedStore = { ...store, ...payload };
        localStorage.setItem("storeInfo", JSON.stringify(updatedStore));
        dispatch(
          showToast({
            type: "success",
            title: "Success",
            message: "Header, theme & GST settings updated!",
          })
        );
      }
    } catch (err) {
      dispatch(
        showToast({
          type: "error",
          title: "Error",
          message: "Failed to update settings.",
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  return (
    <div className="d-flex flex-column gap-4 bg-light mt-3 p-3 rounded">
      <div>
        <h3>Header & Favicon</h3>
        <p className="text-muted">
          Customize your shop logo, favicon, and brand color palette.
        </p>
      </div>

      {/* Logo Section */}
      <div className="card p-3 border-0 shadow-sm">
        <h5 className="mb-3">Shop Logo</h5>
        <div className="d-flex align-items-center gap-4">
          <div
            className="border rounded p-2 d-flex align-items-center justify-content-center bg-white"
            style={{ width: "100px", height: "100px" }}
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Shop Logo"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <span className="text-muted fs-4 fw-bold">
                {formData.name?.substring(0, 2).toUpperCase() || "LOGO"}
              </span>
            )}
          </div>
          <div>
            <label className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 cursor-pointer">
              <Upload size={16} /> Upload New Logo
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: "none" }}
              />
            </label>
            <small className="text-muted d-block mt-2">
              Recommended size: 200x200px (PNG or SVG).
            </small>
          </div>
        </div>
      </div>

      {/* Brand Palette */}
      <div className="card p-3 border-0 shadow-sm">
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <Palette size={18} /> Brand Colors
        </h5>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Primary Color</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="color"
                className="form-control form-control-color"
                name="primaryColor"
                value={formData.primaryColor}
                onChange={handleColorChange}
              />
              <input
                type="text"
                className="form-control"
                name="primaryColor"
                value={formData.primaryColor}
                onChange={handleColorChange}
              />
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Secondary Color</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="color"
                className="form-control form-control-color"
                name="secondaryColor"
                value={formData.secondaryColor}
                onChange={handleColorChange}
              />
              <input
                type="text"
                className="form-control"
                name="secondaryColor"
                value={formData.secondaryColor}
                onChange={handleColorChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* GST & Tax Settings Section */}
      <div className="card p-3 border-0 shadow-sm">
        <h5 className="mb-3 d-flex align-items-center gap-2">
          GST & Tax Configuration
        </h5>
        
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="isGstEnabled"
            name="isGstEnabled"
            checked={taxSettings.isGstEnabled}
            onChange={handleTaxSettingChange}
          />
          <label className="form-check-label fw-semibold" htmlFor="isGstEnabled">
            Enable GST Tax Invoicing for this Restaurant
          </label>
        </div>

        {taxSettings.isGstEnabled && (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">GSTIN Number</label>
              <input
                type="text"
                className="form-control"
                name="gstNumber"
                placeholder="e.g. 33AAAAA0000A1Z5"
                value={taxSettings.gstNumber}
                onChange={handleTaxSettingChange}
              />
              <small className="text-muted">Printed on customer receipts for tax filing.</small>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Default GST Rate (%)</label>
              <input
                type="number"
                min="0"
                max="28"
                className="form-control"
                name="defaultGstRate"
                value={taxSettings.defaultGstRate}
                onChange={handleTaxSettingChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Tax Calculation Mode</label>
              <select
                className="form-select"
                name="gstMode"
                value={taxSettings.gstMode}
                onChange={handleTaxSettingChange}
              >
                <option value="EXCLUSIVE">Exclusive (Add GST on top)</option>
                <option value="INCLUSIVE">Inclusive (Tax included in price)</option>
              </select>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Button label="Save Changes" onClick={handleSaveTheme} />
        </div>
      </div>

      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropImageSrc}
        onClose={() => setIsCropOpen(false)}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
};

export default HeaderFavicon;
